'use client'

import { Img } from 'components/basic/Img'
import { Type } from 'components/Type'
import { FC } from 'react'
import { useFinancingProvider } from 'stores/prices'
import styles from './FinancingGuide.module.scss'
import { Card, FinancingGuideProps } from './FinancingGuide.types'

export const FinancingGuideConnected: FC<{ financingProvider?: 'affirm' | 'klarna' | 'truemed' }> = ({ financingProvider }) => {
	const defaultFinancingProvider = useFinancingProvider()
	const activeFinancingProvider = financingProvider ?? defaultFinancingProvider

	if (activeFinancingProvider === 'none') {
		return null
	}

	return <FinancingGuide financingProvider={activeFinancingProvider} />
}

export const FinancingGuide: FC<FinancingGuideProps> = (props) => {
	const { cards = getDefaultCards(props.financingProvider) } = props
	return (
		<div className={styles.container}>
			{cards.map((card) => (
				<div
					className={styles.card}
					key={card.title}
				>
					<Img
						src={card.image.src}
						alt={card.image.alt}
						className={styles.image}
						objectFit="cover"
					/>
					<div className={styles.content}>
						<Type.Headline2 as="h2">{card.title}</Type.Headline2>
						<ul>
							{card.items.map((item) => (
								<li key={item.title}>
									<Type.Headline6 as="h3">{item.title}</Type.Headline6>
									<Type.Body1>{item.description}</Type.Body1>
								</li>
							))}
						</ul>
					</div>
				</div>
			))}
		</div>
	)
}

const getDefaultCards = (financingProvider?: 'affirm' | 'klarna' | 'truemed'): Card[] => {
	if (financingProvider === 'truemed') return truemedCards()

	return [
		{
			image: { src: 'https://res.cloudinary.com/eightsleep/image/upload/new%20york%20bedroom%204k.jpg', alt: 'Financing guide' },
			title: 'Why choose financing?',
			items: [
				{
					title: financingProvider === 'affirm' ? 'Buy now, pay later' : 'Buy now, pay in 3',
					description: 'No need to wait to start sleeping better',
				},
				{
					title: financingProvider === 'affirm' ? 'Spread your payments between 6 and 36 months' : 'Spread your payment over three months',
					description: 'Flexible options that fit your budget',
				},
				{
					title: 'No hidden fees or surprises',
					description: 'Clear terms, upfront',
				},
			],
		},
		{
			image: { src: 'https://res.cloudinary.com/eightsleep/image/upload/new%20york%20bedroom%204k.jpg', alt: 'Financing guide' },
			title: financingProvider === 'affirm' ? 'How to pay with Affirm' : 'How to pay with Klarna',
			items: [
				{
					title: 'Add to cart',
					description: `Choose 'Finance with ${financingProvider === 'affirm' ? 'Affirm' : 'Klarna'}' when selecting a Pod model before checkout`,
				},
				{
					title: 'Pick a payment method',
					description: `Set up easy, automatic payments ${financingProvider === 'affirm' ? 'from 6 to 36 months' : 'over three months'}`,
				},
				{
					title: 'Start your sleep fitness journey',
					description: 'Sleep better everyday with automatic monthly payments',
				},
			],
		},
	]
}

const truemedCards = (): Card[] => {
	return [
		{
			image: { src: 'https://res.cloudinary.com/eightsleep/image/upload/new%20york%20bedroom%204k.jpg', alt: 'Financing guide' },
			title: 'Pay with pre-tax dollars',
			items: [
				{
					title: '',
					description:
						'Truemed lets you use your pre-tax FSA or HSA dollars to buy health-related products like the Pod. If eligible, your purchase can be reimbursed – helping you invest in better sleep using pre-tax dollars.',
				},
			],
		},
		{
			image: { src: 'https://res.cloudinary.com/eightsleep/image/upload/new%20york%20bedroom%204k.jpg', alt: 'Financing guide' },
			title: 'Why use Truemed?',
			items: [
				{
					title: 'Use FSA/HSA funds',
					description: 'Get reimbursed using money you’ve already set aside for health',
				},
				{
					title: 'Save on taxes',
					description: 'Buy a health-eligible product without dipping into your main budget',
				},
				{
					title: 'No paperwork hassle',
					description: 'Truemed handles the approval and processing',
				},
			],
		},
		{
			image: { src: 'https://res.cloudinary.com/eightsleep/image/upload/new%20york%20bedroom%204k.jpg', alt: 'Financing guide' },
			title: 'How to pay with Truemed',
			items: [
				{
					title: 'Add to cart',
					description: 'Choose ‘Pay with FSA/HSA’ at checkout',
				},
				{
					title: 'Take eligibility quiz',
					description: 'Take a quick health survey to determine eligibility',
				},
				{
					title: 'Pay with FSA/HSA card or get reimbursed',
					description: 'If approved, pay with your FSA/HSA card or use a credit card for reimbursement through your provider',
				},
			],
		},
	]
}
